/**
 * Decides which optional sections of a calendar block are shown when vertical
 * space is tight. Visual order (top→bottom) is Title, Time, Location, Notes,
 * Tags — but the *keep* priority the user specified is
 * Title > Time > Location > Tags > Notes (Notes drops first, even though it
 * renders above Tags). Title is always shown (highest priority); this helper
 * only governs the four optional sections.
 */

export interface ContentPresence {
  /** Block height available for content, in px (already net of padding). */
  budgetPx: number;
  /** Measured title height in px (it can wrap to multiple lines). */
  titlePx: number;
  hasLocation: boolean;
  hasNotes: boolean;
  hasTags: boolean;
}

export interface VisibleSections {
  time: boolean;
  location: boolean;
  notes: boolean;
  tags: boolean;
}

/** Approximate rendered heights (px) of the single-line sections at the block's
 *  small font sizes. Notes is variable, so it only needs one line to count as
 *  "shown" and then fills whatever remains. */
const LINE = { time: 13, location: 13, tags: 17, notesMin: 13 } as const;

/** Greedily allocate the budget in keep-priority order. */
export function fitSections(p: ContentPresence): VisibleSections {
  let remaining = p.budgetPx - p.titlePx;
  const take = (need: number, has = true): boolean => {
    if (!has || remaining < need) return false;
    remaining -= need;
    return true;
  };

  // Priority: Time → Location → Tags → Notes.
  const time = take(LINE.time);
  const location = take(LINE.location, p.hasLocation);
  const tags = take(LINE.tags, p.hasTags);
  const notes = take(LINE.notesMin, p.hasNotes);

  return { time, location, notes, tags };
}

/** Everything visible — used when a block is hover-expanded (budget bypassed). */
export const ALL_VISIBLE: VisibleSections = { time: true, location: true, notes: true, tags: true };

/** Title renders at Tailwind `text-xs` (12px · app text scale) with
 *  `leading-tight` (1.25). */
export const TITLE_BASE_PX = 12;
export const TITLE_LINE_RATIO = 1.25;
/** Lower boundary for the auto-shrunk title (· app text scale). */
export const TITLE_MIN_PX = 8;
/** Block wrapper's normal vertical padding per side (`py-0.5`). */
export const BLOCK_V_PAD_PX = 2;
/** Top + bottom 1px borders — the wrapper clips content inside them. */
export const BLOCK_V_CHROME_PX = 2;

export interface TitleFit {
  /** Per-side vertical padding, px; null = keep the `py-0.5` class default. */
  padPx: number | null;
  /** Inline title font-size, px; null = the normal rem-based `text-xs`. */
  fontPx: number | null;
}

/**
 * Title auto-shrink for very short blocks (week/day view at low zoom / short
 * durations): when the block can't show ONE full title line at the normal
 * size, first collapse the wrapper's vertical padding toward 0, and only if
 * that still isn't enough shrink the font to the largest size whose line fits
 * — clamped to TITLE_MIN_PX so a sliver block stays legible. Nulls = normal.
 */
export function fitTitle(blockHeightPx: number, textScale = 1): TitleFit {
  const line = TITLE_BASE_PX * textScale * TITLE_LINE_RATIO;
  const inner = blockHeightPx - BLOCK_V_CHROME_PX;
  if (inner - 2 * BLOCK_V_PAD_PX >= line) return { padPx: null, fontPx: null };
  const padPx = Math.max(0, Math.min(BLOCK_V_PAD_PX, (inner - line) / 2));
  const budget = inner - 2 * padPx;
  const fontPx = budget >= line ? null : Math.max(TITLE_MIN_PX * textScale, budget / TITLE_LINE_RATIO);
  return { padPx, fontPx };
}
